import { Session } from '@ais-ui/auth';
import { ShipInfoByPropertiesProps, FindTargetResult } from '../shipInfo';
import {
  ExtractedTargetInfo,
  extractTargetInfos,
  QueryPointResult,
} from '../extractTargetInfo';
import { trackOperation } from '@ais-ui/performance-tracking';

export const getTargetInfoAtPixel = async (
  session: Session,
  x: number,
  y: number,
  selectTarget: boolean,
  deselectOtherTargets: boolean,
): Promise<ExtractedTargetInfo | null> => {
  const response: QueryPointResult = await session.request('queryPoint', {
    x: x,
    y: y,
    selectTarget: selectTarget,
    deselectOtherTargets: deselectOtherTargets,
  });

  const targetInfos = extractTargetInfos(response);
  if (targetInfos.extractedTargetInfo.length === 0) {
    return null;
  } else {
    return targetInfos;
  }
};

const toNonEmptyString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'number') {
    if (value === 0 || Number.isNaN(value)) {
      return '';
    }
  }
  if (value === '') {
    return '';
  }
  return String(value);
};

export const findTarget = async (
  session: Session,
  props: ShipInfoByPropertiesProps | null,
): Promise<FindTargetResult | null> => {
  if (!props) {
    return null;
  }

  const { type, mmsi, imo, name, callSign } = props;

  const parsedImo = typeof imo === 'string' ? parseInt(imo, 10) : imo || -1;
  const imoInteger = isNaN(parsedImo) ? -1 : parsedImo;

  // The backend expects all string params to be actual strings, never null/undefined
  const stringType = toNonEmptyString(type);
  const stringMmsi = toNonEmptyString(mmsi);
  const stringCallSign = toNonEmptyString(callSign);
  const stringName = toNonEmptyString(name);

  const force = props.forceView === true || props.filteredOut === true;

  const response = await session.request('findTarget', {
    type: stringType,
    mmsi: stringMmsi,
    imo: imoInteger,
    name: stringName,
    callSign: stringCallSign,
    force,
  });

  if (response.result === 'FindTargetNotFound') {
    return { found: false, reason: 'no-target' };
  }

  if (!Number.isFinite(response.lat) || !Number.isFinite(response.lon)) {
    return { found: false, reason: 'no-position' };
  }

  return {
    found: true,
    lat: response.lat,
    lon: response.lon,
    x: response.x,
    y: response.y,
  };
};

/**
 * Attempts to find a target using all provided properties.
 * If the first attempt fails with 'no-target' and a name was provided,
 * retries without the name — vessels sometimes change their reported
 * name in AIS messages, causing a mismatch with what the caller has.
 */
export const findTargetWithNameFallback = async (
  session: Session,
  props: ShipInfoByPropertiesProps,
): Promise<FindTargetResult | null> => {
  const result = await findTarget(session, props);

  const nameWasProvided = toNonEmptyString(props.name) !== '';
  const failedBecauseOfPossibleNameMismatch =
    result && !result.found && result.reason === 'no-target' && nameWasProvided;

  if (!failedBecauseOfPossibleNameMismatch) {
    return result;
  }

  return findTarget(session, { ...props, name: null });
};

const _getShipInfo = async (session: Session): Promise<any> => {
  const response = await session.request('getTargetInfo', {});
  return response;
};

export const getShipInfo = (session: Session): Promise<any> => {
  return trackOperation(() => _getShipInfo(session), 'ship.get', 'data.fetch', {
    resourceType: 'shipInfo',
    transport: 'websocket',
    method: 'getTargetInfo',
  })();
};
